<script>
import { useHouseholdStore } from "@/javascript/stores/household";
import { useOrganizationStore } from "@/javascript/stores/organizations";
import { useAnalyticEventStore } from "@/javascript/stores/analyticEvents";
import { useRoute, useRouter } from "vue-router";
import HouseholdEditDrawer from "./HouseholdEditDrawer.vue";
import HouseholdCreateDrawer from "./HouseholdCreateDrawer.vue";
import HouseholdMap from "./HouseholdMap.vue";
import Index from "@/javascript/views/components/dashboard/Index.vue";
import HeaderBar from "@/javascript/views/components/HeaderBar.vue";

export default {
  components: { HouseholdEditDrawer, HouseholdCreateDrawer, HouseholdMap, Index, HeaderBar },
  setup() {
    const householdStore = useHouseholdStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();
    const route = useRoute();
    const router = useRouter();
    return { householdStore, organizationStore, analyticEventStore, route, router };
  },
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      availableYears: [],
      reportLoading: false,
      reportError: null,
      yearLoading: false,
      notes: '',
      savedNotes: '',
      notesSaving: false,
      editingNotes: false,
    };
  },
  computed: {
    householdId() {
      return this.route.params.id;
    },
    household() {
      return this.householdStore.currentRecord;
    },
    returnToReview() {
      return this.route.query.returnToReview;
    },
    returnToDuplicates() {
      return this.route.query.returnToDuplicates === "true";
    }
  },
  watch: {
    householdId(newId) {
      if (newId) {
        this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
        this.analyticEventStore.logEvent({ action: 'household-view-detail' });
        this.householdStore.show(newId).then((data) => {
          const currentYear = new Date().getFullYear();
          const years = data.available_years || [];
          if (!years.includes(currentYear)) {
            years.unshift(currentYear);
          }
          this.availableYears = years;
          this.notes = data.notes || '';
          this.savedNotes = data.notes || '';
        });
      } else {
        this.householdStore.getHouseholds();
      }
    }
  },
  mounted() {
    this.householdStore.setOrganizationId(this.organizationStore.organizationId);
    if (this.householdId) {
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'household-view-detail' });
      this.householdStore.show(this.householdId).then((data) => {
        const currentYear = new Date().getFullYear();
        const years = data.available_years || [];
        if (!years.includes(currentYear)) {
          years.unshift(currentYear);
        }
        this.availableYears = years;
        this.notes = data.notes || '';
        this.savedNotes = data.notes || '';
      });
    } else {
      this.householdStore.getHouseholds();
    }
  },
  methods: {
    goBack() {
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'household-back' });
      this.router.push({ name: "client-dashboard", params: { ...this.route.params, panel: "households", id: undefined } });
    },
    editHousehold() {
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'household-edit-open' });
      this.$refs.editDrawer.show();
    },
    archiveHousehold() {
      // With the new structure of households, clients will not be able to archive a household with this button.
      // Households need to be empty in order to delete. But you are not able to remvoe all members currently
      // Leaving the else if for now
      // A household will only be empty if someone moves to another household leaving their last one empty.
      if(this.householdStore.currentMembers.length > 0){
        alert("Please remove all members before archiving household");
      }else if(confirm("Archive this household? All members will be removed. Their donor records will not be affected.")) {
        this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
        this.analyticEventStore.logEvent({ action: 'household-archived' });
        this.householdStore.destroy(this.householdId).then(() => {
          this.goBack();
        });
      }
    },
    onSaved() {
      this.householdStore.show(this.householdId);
    },
    formatMoney(cents) {
      if (cents === null || cents === undefined) return "—";
      return "$" + (cents / 100).toLocaleString();
    },
    changeYear() {
      this.yearLoading = true;
      const orgId = this.organizationStore.organizationId;
      fetch(`/organizations/${orgId}/households/${this.householdId}?year=${this.selectedYear}`, {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      })
        // Error Handling
        .then(res => {
          if (!res.ok) throw new Error("Failed to load year data");
          return res.json();
        })
        .then(data => {
          this.householdStore.currentRecord.total_giving = data.total_giving;
          this.householdStore.currentMembers = data.members || [];
        })
        .catch(err => {
          console.error("changeYear failed:", err);
        })
        .finally(() => {
          this.yearLoading = false;
        });
    },
    downloadReport() {
      this.reportLoading = true;
      this.reportError = null;
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'household-download-report' });
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const orgId = this.organizationStore.organizationId;

      fetch(`/organizations/${orgId}/households/${this.householdId}/report?year=${this.selectedYear}`, {
        method: "POST",
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken }
      })
        .then(res => {
          if (!res.ok) return res.json().then(data => { throw new Error(data.error || "Failed to generate report"); });
          return res.json();
        })
        .then(data => {
          const reportId = data.report?.id;
          if (!reportId) throw new Error("No report ID returned");
          return this.pollReportStatus(reportId);
        })
        .then(reportId => {
          const link = document.createElement("a");
          link.href = `/organizations/${orgId}/reports/${reportId}/download_report`;
          link.download = `household_tax_year_${this.selectedYear}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.reportLoading = false;
        })
        .catch(err => {
          this.reportError = err.message || "Failed to generate report.";
          this.reportLoading = false;
        });
    },
    pollReportStatus(reportId, attempt = 1, maxAttempts = 20) {
      const orgId = this.organizationStore.organizationId;
      return new Promise((resolve, reject) => {
        const check = () => {
          fetch(`/organizations/${orgId}/reports/${reportId}`, {
            headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
          })
            .then(res => res.json())
            .then(data => {
              const status = data.record?.status || data.data?.status;
              if (status === "COMPLETED") resolve(reportId);
              else if (status === "FAILED") reject(new Error("Report generation failed"));
              else if (attempt >= maxAttempts) reject(new Error("Report generation timed out"));
              else { attempt++; setTimeout(check, 2000); }
            })
            .catch(reject);
        };
        check();
      });
    },
    saveNotes() {
      this.notesSaving = true;
      this.householdStore.update(this.householdId, { notes: this.notes })
        .then(() => {
          this.savedNotes = this.notes;
          this.editingNotes = false;
        })
        .finally(() => {
          this.notesSaving = false;
        });
    },
    cancelEditNotes() {
      this.notes = this.savedNotes;
      this.editingNotes = false;
    },
    backToDonorReview(){
      const routeName = this.route.name || "client-dashboard";
      this.router.push({
        name: routeName,
        params: { ...this.route.params, panel: "duplicate-donors", id: this.returnToReview },
      });
    },
    backToDuplicatesIndex(){
      const routeName = this.route.name || "client-dashboard";
      const params = { ...this.route.params, panel: "duplicate-donors" };
      delete params.id;
      this.router.push({ name: routeName, params });
    },
  }
};
</script>

<template>
  <div class="w-full">
    <!-- List View (no ID selected) -->
    <div v-if="!householdId">
      <div class="px-4">
        <header-bar title="Households">
          <template #buttons>
            <button
              class="btn btn-outline text-sm"
              @click="$refs.createDrawer.show()"
            >
              Create Household
            </button>
          </template>
        </header-bar>
      </div>
      <div class="mt-2 pt-2 w-full p-4 mx-auto">
        <div class="breakpoint-container relative rounded-xl overflow-hidden border border-gray-300 text-xs">
          <index :model-store="householdStore" />
        </div>
      </div>

      <!-- Create Drawer -->
      <household-create-drawer ref="createDrawer" />
    </div>

    <!-- Detail View (ID selected) -->
    <div
      v-else
      class="p-8 bg-white rounded-xl shadow-lg min-h-screen"
    >
      <!-- Loading -->
      <div
        v-if="householdStore.loadingRecord"
        class="text-center py-12 text-gray-500"
      >
        Loading...
      </div>

      <!-- Show Page -->
      <div
        v-else
        class="space-y-6"
      >
        <!-- Header -->
        <div class="flex justify-between items-start">
          <div>
            <button
              v-if="returnToReview"
              class="text-sm text-gray-500 hover:text-gray-700 mb-2"
              @click="backToDonorReview"
            >
              ← Back to Donor Review
            </button>
            <button
              v-else-if="returnToDuplicates"
              class="text-sm text-gray-500 hover:text-gray-700 mb-2"
              @click="backToDuplicatesIndex"
            >
              ← Back to Duplicate Donors
            </button>
            <button
              v-else
              class="text-sm text-gray-500 hover:text-gray-700 mb-2"
              @click="goBack"
            >
              ← Back to Households
            </button>
            <h1 class="text-2xl font-bold">
              {{ household.name }}
            </h1>
            <p
              v-if="household.address_street"
              class="text-gray-600 mt-1"
            >
              {{ household.address_street }}, {{ household.address_city }}, {{ household.address_state }} {{ household.address_postal_code }}
            </p>
            <div class="flex items-center gap-3 mt-2">
              <p class="text-lg font-semibold text-green-600">
                Household Giving:
              </p>
              <p
                v-if="!yearLoading"
                class="text-lg font-semibold text-green-600"
              >
                {{ formatMoney(household.total_giving) }}
              </p>
              <p
                v-else
                class="text-sm font-semibold text-gray-500 animate-pulse"
              >
                Loading...
              </p>
              <select
                v-model="selectedYear"
                class="text-sm border border-gray-300 rounded px-2 py-1"
                @change="changeYear"
              >
                <option
                  v-for="year in availableYears"
                  :key="year"
                  :value="year"
                >
                  {{ year }}
                </option>
              </select>
            </div>
          </div>
          <div class="flex gap-2">
            <button
              class="btn btn-outline px-4 py-2 text-sm"
              @click="editHousehold"
            >
              Edit
            </button>
            <button
              v-if="householdStore.permissions?.destroy"
              class="btn btn-outline border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              @click="archiveHousehold"
            >
              Archive
            </button>
          </div>
        </div>


        <!-- Members + Map -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 class="text-lg font-semibold mb-3">
              Members
            </h2>
            <div class="space-y-2 max-h-80 overflow-y-auto">
              <div
                v-for="member in householdStore.currentMembers"
                :key="member.id"
                class="border rounded-lg p-4 hover:bg-gray-50"
                :class="member.donor_id ? 'cursor-pointer' : ''"
                @click="member.donor_id && router.push({ name: 'client-dashboard', params: { ...route.params, panel: 'donors', id: member.donor_id } })"
              >
                <p class="font-semibold">
                  {{ member.name }}
                </p>
                <p class="text-sm text-gray-500">
                  {{ member.role }}
                </p>
                <p class="text-sm text-gray-400">
                  {{ member.email }}
                </p>
                <p
                  v-if="!member.has_donor_in_org"
                  class="text-xs text-orange-500 mt-1"
                >
                  Hasn't made a donation to this organization yet
                </p>
                <p
                  v-else-if="member.giving_total"
                  class="text-xs text-green-600 mt-1"
                >
                  {{ formatMoney(member.giving_total) }} in {{ selectedYear }}
                </p>
              </div>
            </div>
            <button
              class="btn btn-outline mt-3 px-4 py-2 text-sm"
              @click="editHousehold"
            >
              + Add Member
            </button>
          </div>
          <div>
            <h2 class="text-lg font-semibold mb-3">
              Map
            </h2>
            <household-map
              :street="household.address_street"
              :city="household.address_city"
              :state="household.address_state"
              :postal-code="household.address_postal_code"
              :country="household.address_country"
            />
          </div>
        </div>

        <!-- Notes -->
        <div class="border rounded-lg p-4">
          <div class="flex justify-between items-center mb-3">
            <h2 class="text-lg font-semibold">
              Notes
            </h2>
            <button
              v-if="householdStore.permissions?.updated && !editingNotes"
              class="btn btn-outline px-3 py-1 text-xs"
              @click="editingNotes = true"
            >
              {{ savedNotes ? 'Edit' : 'Add Note' }}
            </button>
          </div>
          <p class="text-xs text-gray-400 mb-2">
            Internal — only visible to organization staff.
          </p>

          <!-- Read-only view -->
          <div v-if="!editingNotes">
            <p
              v-if="savedNotes"
              class="text-sm text-gray-700 whitespace-pre-wrap max-h-96 overflow-y-auto"
            >
              {{ savedNotes }}
            </p>
            <p
              v-else
              class="text-sm text-gray-400 italic"
            >
              No notes yet.
            </p>
          </div>

          <!-- Edit view -->
          <div v-else>
            <textarea
              v-model="notes"
              class="w-full border border-gray-300 rounded-md p-3 text-sm min-h-48 max-h-96 overflow-y-auto resize-none focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="Add notes about this household..."
            />
            <div class="flex items-center gap-2 mt-2">
              <button
                class="btn btn-outline px-4 py-2 text-sm"
                :disabled="notesSaving"
                @click="saveNotes"
              >
                {{ notesSaving ? 'Saving...' : 'Save' }}
              </button>
              <button
                class="btn btn-outline px-4 py-2 text-sm text-gray-500"
                :disabled="notesSaving"
                @click="cancelEditNotes"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <!-- Tax Year Report -->
        <div class="border rounded-lg p-4">
          <h2 class="text-lg font-semibold mb-3">
            Tax Year Report
          </h2>
          <p class="text-sm text-gray-500 mb-3">
            Generate a PDF summary of this household's giving for a tax year.
          </p>
          <div class="flex items-center gap-4">
            <select
              v-model="selectedYear"
              class="text-sm border border-gray-300 rounded px-2 py-1"
              @change="changeYear"
            >
              <option
                v-for="year in availableYears"
                :key="year"
                :value="year"
              >
                {{ year }}
              </option>
            </select>
            <button
              class="btn btn-outline px-4 py-2 text-sm"
              :disabled="reportLoading"
              @click="downloadReport"
            >
              {{ reportLoading ? 'Generating...' : 'Download PDF' }}
            </button>
          </div>
          <p
            v-if="reportError"
            class="text-xs text-red-500 mt-2"
          >
            {{ reportError }}
          </p>
        </div>

        <!-- Donation History -->
        <div>
          <h2 class="text-lg font-semibold mb-3">
            Donation History
          </h2>
          <div
            v-if="householdStore.currentDonations.length === 0"
            class="text-gray-500 text-sm"
          >
            No donations yet.
          </div>
          <table
            v-else
            class="w-full text-sm"
          >
            <thead>
              <tr class="border-b text-left text-gray-500">
                <th class="py-2">
                  Date
                </th>
                <th class="py-2">
                  Member
                </th>
                <th class="py-2">
                  Fund/Campaign
                </th>
                <th class="py-2">
                  Amount
                </th>
                <th class="py-2">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              <!--   Clickable rows that lead to gift details page-->
              <tr
                v-for="(donation, i) in householdStore.currentDonations"
                :key="i"
                class="border-b cursor-pointer hover:bg-gray-50"
                @click="router.push({ name: 'client-dashboard', params: { ...route.params, panel: 'gifts', id: donation.id } })"
              >
                <td class="py-2">
                  {{ donation.date }}
                </td>
                <td class="py-2">
                  {{ donation.member_name }}
                </td>
                <td class="py-2">
                  {{ donation.designation }}
                </td>
                <td class="py-2">
                  {{ formatMoney(donation.amount) }}
                </td>
                <td class="py-2">
                  {{ donation.status }}
                </td>
              </tr>
            </tbody>
          </table>
          <button
            v-if="householdStore.donationMeta.page < householdStore.donationMeta.pages"
            class="text-xs"
            @click="householdStore.loadMoreDonations(householdId)"
          >
            [Load More]
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Drawer -->
  <household-edit-drawer
    ref="editDrawer"
    :household="household"
    :members="householdStore.currentMembers"
    @saved="onSaved"
  />
</template>

<style scoped>
:deep(tbody tr) {
  cursor: pointer;
}
</style>
